/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useState, useEffect } from 'react';
import Modal from 'react-modal';
import { PurchaseOfferData } from '@upstart/harmony/lib/legos/deskingTypes';
import { FieldToAdjust } from '@upstart/harmony/lib/legos/paymentRollbackTypes';
import DeskingService from 'services/DeskingService';
import { cloneDeep } from 'lodash';
import { Dimmer, Loader } from 'semantic-ui-react';
import { PriceInput } from 'components/PencilEditor/Input';
import { useDebounce } from 'hooks/useDebounce';
import { modalOverlayBackgroundColor } from 'util/constants';
import CloseIcon from '../../../Icons/Close';
import { PaymentRollbackOfferSummary } from './PaymentRollbackOfferSummary';
import './PaymentRollbackModal.scss';
import {
  PAYMENT_ROLLBACK_DEBOUNCE_WAIT_IN_MS,
  PAYMENT_ROLLBACK_DEFAULT_MESSAGE_IF_NO_OFFER_EXISTS,
  PAYMENT_ROLLBACK_ERROR,
  PAYMENT_ROLLBACK_INVALID_NUMBER_ERROR,
} from './constants';
import { PaymentRollbackModalProps, RollbackOfferSummary } from './types';

const scrollableModalStyles = {
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: modalOverlayBackgroundColor,
  },
  content: {
    top: 'unset',
    bottom: 'unset',
    left: 'unset',
    right: 'unset',
    maxWidth: '50vw',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
} as const;

const PaymentRollbackModal: FunctionComponent<PaymentRollbackModalProps> = props => {
  const {
    deskingOffer,
    isOpen,
    onClose,
    months,
    monthlyPayment,
    onPaymentRollbackChange,
    dealer,
  } = props;

  const [targetedMonthlyPayment, setTargetedMonthlyPayment] = useState<
    number
  >();
  const [fieldToAdjust, setFieldToAdjust] = useState<FieldToAdjust>(
    FieldToAdjust.DOWNPAYMENT,
  );
  const [
    rollbackOfferSummary,
    setRollbackOfferSummary,
  ] = useState<RollbackOfferSummary | null>(null);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleDecimalsOnValue = (value: string | undefined) => {
    if (!value) {
      return undefined;
    }
    const regex = /([0-9]*[.]{0,1}[0-9]{0,2})/s;
    return +value.match(regex)![0];
  };

  const getPaymentRollbackOffer = async () => {
    try {
      if (!targetedMonthlyPayment) {
        return;
      }

      setLoading(true);
      const clonedDeskingOffer = cloneDeep(deskingOffer);

      const paymentRollbackOffer = await DeskingService.paymentRollback({
        fieldToAdjust,
        deskingOffer: clonedDeskingOffer,
        currentMonthlyPayment: monthlyPayment?.[0],
        targetedMonthlyPayment,
        dealerId: dealer.id,
      });
      setRollbackOfferSummary({
        monthlyPayment: paymentRollbackOffer?.estimatedMonthlyPayment,
        price: paymentRollbackOffer?.price,
        trade: paymentRollbackOffer?.tradeInAllowance,
        downPayment: paymentRollbackOffer?.downPayment,
        apr: paymentRollbackOffer?.apr,
      });
      setError(undefined);
      setLoading(false);
    } catch (err) {
      if ((err as any).error) {
        try {
          const parsedError = JSON.parse((err as any).error);
          // ignore parsedError.error if it is empty object
          if (parsedError.error) {
            setError(parsedError.error);
          }
        } catch (e) {
          const errorMessage = PAYMENT_ROLLBACK_ERROR;
          // eslint-disable-next-line no-console
          console.error(errorMessage, e);
        }
      }
      setRollbackOfferSummary(null);
      setLoading(false);
    }
  };

  const debouncedRequest = useDebounce(
    getPaymentRollbackOffer,
    PAYMENT_ROLLBACK_DEBOUNCE_WAIT_IN_MS,
  );

  const onChange = (val: string | undefined) => {
    const filteredValue = handleDecimalsOnValue(val);
    if (!filteredValue) {
      setError(PAYMENT_ROLLBACK_INVALID_NUMBER_ERROR);
    }
    setTargetedMonthlyPayment(filteredValue);
    debouncedRequest();
  };

  useEffect(() => {
    getPaymentRollbackOffer();
  }, [fieldToAdjust]);

  const onChangeForRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldToAdjust(event.target.value as FieldToAdjust);
  };

  const onModelClose = () => {
    setTargetedMonthlyPayment(undefined);
    setFieldToAdjust(FieldToAdjust.DOWNPAYMENT);
    setRollbackOfferSummary(null);
    setError(undefined);
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Payment Rollback"
      onRequestClose={onModelClose}
      shouldCloseOnOverlayClick
      style={scrollableModalStyles}
      ariaHideApp={false}
    >
      <CloseIcon onClick={onModelClose} />
      <div className="payment-rollback-model">
        <div className="payment-rollback-header capitalize-in-chairman">
          Enter rollback details {months ? `for ${months} months` : ''}
        </div>

        <div className="payment-rollback-top-section">
          <div className="payment-rollback-top-section-inputs-section">
            <div className="payment-rollback-targeted-monthly-payment-input">
              <label>New Rollback Payment</label>
              <PriceInput
                data-testid="PaymentRollback-TargetedMonthlyPayment"
                value={targetedMonthlyPayment}
                placeholder="Targeted Monthly Payment"
                onChange={val => onChange(val)}
                displayDollarSign
                className="payment-rollback-input"
                step="0.01"
                isInputModeDecimal
                disabled={loading}
              />
            </div>

            <div className="radio-inputs-section">
              <label>Fields to Adjust</label>
              <div className="adjust-radio">
                <label>
                  <input
                    data-testid="PaymentRollback-DownPayment-radio"
                    type="radio"
                    name="pps"
                    value={FieldToAdjust.DOWNPAYMENT}
                    checked={fieldToAdjust === FieldToAdjust.DOWNPAYMENT}
                    onChange={onChangeForRadio}
                    className="form-check-input"
                    disabled={loading}
                  />
                  Down Payment
                </label>
                <label>
                  <input
                    data-testid="PaymentRollback-SellingPrice-radio"
                    type="radio"
                    name="pps"
                    value={FieldToAdjust.SELLING_PRICING}
                    checked={fieldToAdjust === FieldToAdjust.SELLING_PRICING}
                    onChange={onChangeForRadio}
                    className="form-check-input"
                    disabled={loading}
                  />
                  Selling Price
                </label>
              </div>
            </div>
          </div>
          {error && (
            <div
              className="payment-rollback-top-section-error-section"
              data-testid="PaymentRollback-Error"
            >
              {error}
            </div>
          )}
        </div>

        <div className="payment-rollback-bottom-section">
          <div className="payment-rollback-bottom-section-left-section">
            <PaymentRollbackOfferSummary
              label="Current Offer"
              offerSummary={{
                monthlyPayment: monthlyPayment?.[0],
                price: deskingOffer.data?.price,
                trade: deskingOffer.data?.tradeInAllowance,
                downPayment: deskingOffer.data?.downPayment,
                apr: (deskingOffer.data as PurchaseOfferData)?.apr,
              }}
            />
          </div>

          <div className="payment-rollback-bottom-section-right-section">
            <PaymentRollbackOfferSummary
              label="New Rollback Offer"
              message={PAYMENT_ROLLBACK_DEFAULT_MESSAGE_IF_NO_OFFER_EXISTS}
              offerSummary={rollbackOfferSummary}
              fieldToAdjust={fieldToAdjust}
            />
          </div>
        </div>
        <div className="payment-rollback-modal-footer">
          <button
            className="modal-button secondary cc-button-outlined"
            disabled={false}
            onClick={() => onModelClose()}
          >
            Cancel
          </button>
          <button
            key="save"
            className="modal-button cc-button-primary save"
            disabled={!rollbackOfferSummary}
            onClick={() => {
              const field =
                fieldToAdjust === FieldToAdjust.DOWNPAYMENT
                  ? 'downPayment'
                  : 'price';
              onPaymentRollbackChange(
                field,
                'price',
                0,
              )(rollbackOfferSummary?.[field] || 0);
              onModelClose();
            }}
          >
            Apply rollback pricing
          </button>
        </div>
        <Dimmer active={loading} inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      </div>
    </Modal>
  );
};

export default PaymentRollbackModal;
